import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { permissionsEnumSchema } from "@mindlercare/schema";
import { PatientCard } from "components/patients/patientCard";
import { ReferralForm } from "components/patients/patientCard/patientActions/referralForm";
import { useImplementedFeature } from "core/selectors/app";
import { useZendesk } from "hooks/useZendesk";
import { IcbtPayments } from "pages/payments/IcbtPaymentsPage";
import { PaymentsPage } from "pages/payments/PaymentsPage";
import Dashboard from "views/Dashboard";
import { Messages } from "views/Messages";
import Patients from "views/Patients";
import Questionnaires from "views/Questionnaires";
import { Login } from "../components/Login";
import { Maintenance } from "../components/maintenance";
import { PsychologistDashboard } from "../components/psychologistDashboard";
import { Layout } from "./Layout";
import { RequiresAuth } from "./RequiresAuth";
import { RequiresPermissions } from "./RequiresPermissions";
import { RequiresProfileSetup } from "./RequiresProfileSetup";
import { useFlag, useInitUnleash } from "@mindlercare/unleash-client";
import { PsychologistProfile } from "../components/psychologist";
import { IcbtFeedback } from "../components/icbt/feedback/IcbtFeedback";
import { IcbtTasks } from "../components/icbt/feedback/IcbtTasks";
import { IcbtPreviewPage } from "../pages/IcbtPreviewPage";
import { CancelledSlots } from "../pages/CancelledSlotsPage";
import { Booking } from "../components/booking";
import { ChangelogPage } from "pages/Changelog";
import { UserSettingsPage } from "pages/UserSettings";
import { ClinicalGuidelines } from "pages/Chatbot/ClinicalGuidelines";

export const App = () => {
  const [isMaintenanceEnabled] = useImplementedFeature("MAINTENANCE");
  const [isReferralEnabled] = useImplementedFeature("REFERRAL_CERTIFICATE");
  const isPsychologistDashboardEnabled = useFlag("PSYCHOLOGIST_DASHBOARD");
  const isAssessmentEnabled = useFlag("ICBT_ASSESSMENT_FIELD");
  useZendesk();
  useInitUnleash();

  if (isMaintenanceEnabled) return <Maintenance />;

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Layout />}>
        <Route path="/login" element={<Login />} />
        <Route element={<RequiresAuth />}>
          <Route element={<RequiresProfileSetup />}>
            <Route path="/" element={<Booking />} />
            <Route path="patients" element={<Patients />} />
            <Route
              path="patients/psychologist/:psychologistId/patient/:patientUserId/*"
              element={<PatientCard />}
            ></Route>
            <Route path="/messages/:userId/:type?" element={<Messages />} />
            <Route
              path="/questionnaires/:userId"
              element={<Questionnaires />}
            />
            <Route path="/icbt/*" element={<IcbtPreviewPage />} />
            <Route
              path="/icbt-feedback/*"
              element={isAssessmentEnabled ? <IcbtTasks /> : <IcbtFeedback />}
            />

            <Route
              path="/referral-form/patientUserId/:patientUserId"
              element={
                isReferralEnabled ? <ReferralForm /> : <Navigate to="/" />
              }
            />
          </Route>
          <Route path="/dashboard" element={<Dashboard />} />
          {isPsychologistDashboardEnabled && (
            <Route
              path="/psychologist-dashboard"
              element={<PsychologistDashboard />}
            />
          )}
          <Route path="profile" element={<PsychologistProfile />} />
          <Route
            path="/payments"
            element={
              <RequiresPermissions
                permissions={permissionsEnumSchema.enum.USER_PAYMENTS}
              >
                <PaymentsPage />
              </RequiresPermissions>
            }
          />
          <Route
            path="/icbt-payments"
            element={
              <RequiresPermissions
                permissions={permissionsEnumSchema.enum.EXTRACT_ICBT_FEE}
              >
                <IcbtPayments />
              </RequiresPermissions>
            }
          />
          <Route path="/cancelled-bookings" element={<CancelledSlots />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/user-settings" element={<UserSettingsPage />} />
          <Route
            path="/chatbot/clinical-guidelines"
            element={<ClinicalGuidelines />}
          />
        </Route>
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};
